import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp';
import { makeStyles } from '@mui/styles';
import {
  Badge,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs
} from '@mui/material';
import { ApolloQueryResult } from '@apollo/client';
import {
  AllCollectorsQuery,
  CollectorInstallation,
  CollectorInstallError,
  CollectorScope
} from 'graphql/types';
import { useMessages } from 'modules/intl';
import { useSidePanelContext } from 'modules/rootPage';
import { CollectorDetailSidePanel } from './CollectorDetailSidePanel';
import { CollectorManagePage } from './CollectorManagePage';
import CollectorOverviewPage from './CollectorOverviewPage';
import { useUninstallCollector } from './Install/hooks/useUninstallCollector';
import { useDisconnectUserCollector } from './Setting/hooks/useDisconnectUserCollector';
import { infoType } from './AllCollectorsListPage';
import { useCollectorSyncStatusContext } from './contexts/CollectorSyncStatusContextProvider';
import { SyncStatus } from './utils';

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
    background: theme.palette.common.white,
    color: theme.palette.common.black
  },
  appBarText: {
    ...theme.typography.h4,
    fontSize: theme.spacing(2.9),
    marginLeft: theme.spacing(1)
  },
  detailDialog: {
    left: ({ pinned }: { pinned: boolean | undefined }) => (pinned ? 240 : 65),
    zIndex: 100
  },
  detailDialogPaper: {
    overflow: 'hidden'
  },
  toolbar: {
    minHeight: theme.spacing(6)
  },
  container: {
    backgroundColor: theme.palette.information.pageBackground,
    height: 'calc(100vh - 40px)',
    display: 'flex'
  },
  verticalDivider: {
    borderRightWidth: theme.spacing(0.25)
  },
  tab: {
    fontWeight: theme.typography.fontWeightBold,
    width: theme.spacing(20)
  },
  tabToolbar: {
    width: '100%',
    paddingLeft: theme.spacing(0)
  },
  tabs: {
    width: theme.spacing(60),
    alignSelf: 'flex-end'
  },
  div: {
    width: '98%'
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.dark,
      bottom: theme.spacing(1.2),
      left: theme.spacing(-1.2)
    }
  }
}));

const useDialogStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: theme.spacing(62)
  },
  disconnectDialogTitle: {
    color: theme.palette.grey[500]
  },
  disconnectDialogText: {
    color: theme.palette.grey[500]
  },
  disconnectDialogCancelButton: {
    color: theme.palette.common.white
  },
  disconnectIndicator: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(6.5)
  }
}));

type CollectorDetailsProps = {
  open: boolean;
  handleClose: () => void;
  info: infoType | null;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  value: string;
  refetch: (variables?: any) => Promise<ApolloQueryResult<AllCollectorsQuery>>;
  onSnackBarOpen: () => void;
};

type DisconnectDialogProps = {
  dialogOpen: boolean;
  handleDialogClose: () => void;
  detailsMessages: Record<string, string>;
  isDisconnecting: boolean;
  name: string;
  setIsDisconnecting: React.Dispatch<React.SetStateAction<boolean>>;
  collectorId: string;
  refetch: (variables?: any) => Promise<ApolloQueryResult<AllCollectorsQuery>>;
  onSnackBarOpen: () => void;
  handleClose: () => void;
};

const messages = [
  'collectors.overview',
  'collectors.manage',
  'collectors.disconnecting',
  'collectorSetting.disconnectingMessage1',
  'collectorSetting.disconnectingMessage2',
  'collectorSetting.disconnect',
  'collectorSetting.cancel'
];

export const DisconnectDialog: React.FC<DisconnectDialogProps> = ({
  dialogOpen,
  handleDialogClose,
  detailsMessages,
  isDisconnecting,
  name,
  setIsDisconnecting,
  collectorId,
  refetch,
  onSnackBarOpen,
  handleClose
}: DisconnectDialogProps) => {
  const classes = useDialogStyles();

  const { uninstallCollector } = useUninstallCollector();
  const { disconnectUserCollector } = useDisconnectUserCollector();

  const handleDisconnectUser = useCallback(async () => {
    setIsDisconnecting(true);
    const cId = collectorId || '';

    const dataSetPromise = uninstallCollector(cId, CollectorScope.User);
    const disconnectPromise = disconnectUserCollector({
      collectorId: cId
    });
    const [dataSet] = await Promise.all([dataSetPromise, disconnectPromise]);
    if (dataSet?.data?.uninstallUserCollector?.collectorId) {
      await refetch();
      handleDialogClose();
      handleClose();
      onSnackBarOpen();
    }
    setIsDisconnecting(false);
  }, [
    setIsDisconnecting,
    collectorId,
    uninstallCollector,
    disconnectUserCollector,
    refetch,
    handleDialogClose,
    handleClose,
    onSnackBarOpen
  ]);

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      data-testid="disconnecting-dialog"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle
        className={classes.disconnectDialogTitle}
        data-testid="disconnecting-dialog-title"
      >
        {detailsMessages['collectors.disconnecting']} {name}
      </DialogTitle>
      <DialogContent className={classes.disconnectDialogText}>
        <Typography data-testid="disconnecting-dialog-message1">
          {detailsMessages['collectorSetting.disconnectingMessage1']}
        </Typography>
        <br />
        <Typography data-testid="disconnecting-dialog-message2">
          {detailsMessages['collectorSetting.disconnectingMessage2']}
        </Typography>
      </DialogContent>
      <DialogActions>
        {isDisconnecting ? (
          <CircularProgress
            size={22}
            data-testid="disconnect-progress"
            className={classes.disconnectIndicator}
          />
        ) : (
          <Button
            onClick={handleDisconnectUser}
            color="primary"
            disabled={isDisconnecting}
            data-testid="disconnecting-dialog-disconnecting-button"
          >
            {detailsMessages['collectorSetting.disconnect']}
          </Button>
        )}

        <Button
          onClick={handleDialogClose}
          color="secondary"
          variant="contained"
          className={classes.disconnectDialogCancelButton}
          data-testid="disconnecting-dialog-cancel-button"
        >
          {detailsMessages['collectorSetting.cancel']}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CollectorDetails: React.FC<CollectorDetailsProps> = memo(
  ({
    open,
    handleClose,
    info,
    value,
    setValue,
    refetch,
    onSnackBarOpen
  }: CollectorDetailsProps) => {
    const detailsMessages = useMessages(messages);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
    const installationObj = useMemo(() => {
      const obj = (
        (info?.installations?.items as CollectorInstallation[]) || []
      ).reduce((acc, i) => {
        acc[i.scope] = i?.installId;
        return acc;
      }, {} as Record<string, string>);
      return obj;
    }, [info?.installations]);

    const handleDialogClose = useCallback(() => {
      setDialogOpen(false);
    }, []);
    const { collectorsSyncStatus } = useCollectorSyncStatusContext();

    const hasTenantError = useMemo(() => {
      const installId = installationObj[CollectorScope.Tenant];

      if (
        collectorsSyncStatus[installId] &&
        collectorsSyncStatus[installId] !== SyncStatus.completed &&
        collectorsSyncStatus[installId] !== SyncStatus.inProgress
      )
        return true;
      return false;
    }, [collectorsSyncStatus, installationObj]);

    const hasUserError = useMemo(() => {
      const installId = installationObj[CollectorScope.User];

      if (
        collectorsSyncStatus[installId] &&
        collectorsSyncStatus[installId] !== SyncStatus.completed &&
        collectorsSyncStatus[installId] !== SyncStatus.inProgress
      )
        return true;
    }, [collectorsSyncStatus, installationObj]);

    const handleChange = useCallback(
      (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
      },
      [setValue]
    );

    const scopeSet = useMemo(() => {
      const s = new Set(info?.appScope);
      return s as Set<string>;
    }, [info?.appScope]);
    const { pinned } = useSidePanelContext();

    const installationScopeSet = useMemo(() => {
      const s = new Set();
      info?.installations?.items?.map((i) => {
        s.add(i?.scope);
      });
      return s as Set<string>;
    }, [info?.installations?.items]);
    const handleDialogOpen = useCallback(() => {
      setDialogOpen(true);
    }, [setDialogOpen]);

    const installInformation = useMemo(() => {
      const userMaintainers = info?.userInstallationDetails;
      const userInstallDate = info?.userInstallationDate;
      const tenantMaintainers = info?.tenantInstallationDetails;
      const tenantInstallationDate = info?.tenantInstallationDate;
      return {
        userMaintainers,
        userInstallDate,
        tenantMaintainers,
        tenantInstallationDate
      };
    }, [
      info?.tenantInstallationDate,
      info?.tenantInstallationDetails,
      info?.userInstallationDate,
      info?.userInstallationDetails
    ]);
    const onClose = useCallback(() => {
      handleClose();
    }, [handleClose]);

    const classes = useStyles({ pinned });
    return (
      <div>
        <Dialog
          fullScreen
          open={open}
          PaperProps={{ elevation: 0, className: classes.detailDialogPaper }}
          data-testid="collectorDetailsDialog"
          className={classes.detailDialog}
          hideBackdrop={true}
        >
          <AppBar
            className={classes.appBar}
            elevation={0}
            data-testid="collectorAppBar"
          >
            <Toolbar className={classes.toolbar}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={onClose}
                data-testid="collectorBackIcon"
              >
                <ArrowBackSharpIcon />
              </IconButton>
              <Typography
                className={classes.appBarText}
                component="div"
                data-testid="collectorDetailname"
              >
                {info?.name}
              </Typography>
            </Toolbar>
            {installationScopeSet.has(CollectorScope.Tenant) && (
              <Toolbar className={classes.tabToolbar}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="collector tabs"
                  className={classes.tabs}
                >
                  <Tab
                    data-testid="collector_overview"
                    className={classes.tab}
                    label={
                      <Badge
                        variant="dot"
                        className={classes.badge}
                        invisible={!hasUserError}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left'
                        }}
                      >
                        {detailsMessages['collectors.overview']}
                      </Badge>
                    }
                    value={'collectors.overview'}
                  ></Tab>

                  <Tab
                    data-testid="collector_manage"
                    className={classes.tab}
                    label={
                      <Badge
                        color="secondary"
                        variant="dot"
                        className={classes.badge}
                        invisible={!hasTenantError}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left'
                        }}
                      >
                        {detailsMessages['collectors.manage']}
                      </Badge>
                    }
                    value={'collectors.manage'}
                  />
                </Tabs>
              </Toolbar>
            )}
          </AppBar>
          <Divider data-testid="collectorAppBarDivider" />
          <div className={classes.container}>
            <CollectorDetailSidePanel
              info={info}
              handleClose={onClose}
              handleDialogOpen={handleDialogOpen}
              scopeSet={scopeSet}
              installationScopeSet={installationScopeSet}
              installInformation={installInformation}
            />
            <Divider
              orientation="vertical"
              className={classes.verticalDivider}
              data-testid="collectorVerticalDivider"
            />
            <div className={classes.div}>
              {value === 'collectors.overview' ? (
                <CollectorOverviewPage
                  descriptionSrc={info?.description || ''}
                  retryUrl={info?.retryURL as string}
                  installId={installationObj[CollectorScope.User]}
                  error={
                    info?.userInstallStatus?.errors as CollectorInstallError[]
                  }
                />
              ) : (
                <div>
                  <CollectorManagePage
                    installationObj={installationObj}
                    scope={info?.appScope as string[]}
                    retryUrl={info?.retryURL as string}
                    hasTenantError={hasTenantError}
                  />
                </div>
              )}
            </div>
            <DisconnectDialog
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              name={info?.name || ''}
              isDisconnecting={isDisconnecting}
              setIsDisconnecting={setIsDisconnecting}
              detailsMessages={detailsMessages}
              collectorId={info?.id || ''}
              refetch={refetch}
              onSnackBarOpen={onSnackBarOpen}
              handleClose={onClose}
            />
          </div>
        </Dialog>
      </div>
    );
  }
);
CollectorDetails.displayName = 'CollectorDetails';
